// Ambient-occlusion geometry for the presentation look of a placed model: one
// quad per exposed voxel face, with classic 3-neighbor corner AO. The greedy
// mesher can't carry per-corner AO (a merged rectangle has only 4 vertices), so
// this trades more triangles for soft contact shading.
//
// THREE-FREE and COLOR-AGNOSTIC on purpose: this is the heaviest bake pass, so
// it runs in the off-thread bake worker (which must not import three.js). It
// emits geometry plus, per vertex, the source palette index and an AO shade
// multiplier — but NOT a final color. The color is `palette.color(p) * shade`,
// applied wherever THREE is available (model-bake, both the synchronous bake and
// the asset deserialize) via the SAME palette lookup, so the two paths are
// pixel-identical and there's no color-management logic to replicate here.

import { CELL_SIZE, type VoxelGrid } from "../model/voxel-grid";

export interface AoArrays {
  pos: number[]; // xyz per vertex (metres)
  norm: number[]; // face normal per vertex
  pal: number[]; // source palette index per vertex
  shade: number[]; // AO multiplier per vertex (AO_LEVELS)
}

// Corner darkening per occlusion level 0-3 (3 = fully open). The classic
// voxel-AO ramp: subtle on flats, distinct in corners and contacts.
const AO_LEVELS = [0.55, 0.72, 0.86, 1.0];
// Face table: outward normal n, the face's two in-plane axes u/v, and whether
// the (-,-)(+,-)(+,+)(-,+) corner walk needs reversing to stay CCW from outside
// (i.e. when u×v points against n).
const AO_DIRS: { n: number[]; u: number[]; v: number[]; flip: boolean }[] = [
  { n: [1, 0, 0], u: [0, 1, 0], v: [0, 0, 1], flip: false },
  { n: [-1, 0, 0], u: [0, 1, 0], v: [0, 0, 1], flip: true },
  { n: [0, 1, 0], u: [1, 0, 0], v: [0, 0, 1], flip: true },
  { n: [0, -1, 0], u: [1, 0, 0], v: [0, 0, 1], flip: false },
  { n: [0, 0, 1], u: [1, 0, 0], v: [0, 1, 0], flip: false },
  { n: [0, 0, -1], u: [1, 0, 0], v: [0, 1, 0], flip: true },
];

// Build the AO triangle soup for `grid`. Returns null when nothing is exposed.
export function buildAOArrays(grid: VoxelGrid): AoArrays | null {
  const pos: number[] = [];
  const norm: number[] = [];
  const pal: number[] = [];
  const shade: number[] = [];
  const corner = { x: 0, y: 0, z: 0, ao: 0 };
  const corners = [{ ...corner }, { ...corner }, { ...corner }, { ...corner }];
  const SU = [-1, 1, 1, -1];
  const SV = [-1, -1, 1, 1];

  for (const [ix, iy, iz] of grid.entries()) {
    const p = grid.get(ix, iy, iz);
    for (const d of AO_DIRS) {
      const nx = ix + d.n[0];
      const ny = iy + d.n[1];
      const nz = iz + d.n[2];
      if (grid.has(nx, ny, nz)) continue; // face not exposed

      for (let c = 0; c < 4; c++) {
        const su = SU[c];
        const sv = SV[c];
        // Occluders live in the layer the face looks into (the empty neighbor
        // cell), beside/diagonal to the corner.
        const s1 = grid.has(nx + su * d.u[0], ny + su * d.u[1], nz + su * d.u[2]);
        const s2 = grid.has(nx + sv * d.v[0], ny + sv * d.v[1], nz + sv * d.v[2]);
        const sd = grid.has(
          nx + su * d.u[0] + sv * d.v[0],
          ny + su * d.u[1] + sv * d.v[1],
          nz + su * d.u[2] + sv * d.v[2],
        );
        const k = corners[c];
        k.ao = s1 && s2 ? 0 : 3 - ((s1 ? 1 : 0) + (s2 ? 1 : 0) + (sd ? 1 : 0));
        // Corner position in cells: cell min corner + 0/1 per axis. The face
        // plane contributes 1 on the normal axis for positive normals.
        k.x = ix + (d.n[0] > 0 ? 1 : 0) + (su > 0 ? d.u[0] : 0) + (sv > 0 ? d.v[0] : 0);
        k.y = iy + (d.n[1] > 0 ? 1 : 0) + (su > 0 ? d.u[1] : 0) + (sv > 0 ? d.v[1] : 0);
        k.z = iz + (d.n[2] > 0 ? 1 : 0) + (su > 0 ? d.u[2] : 0) + (sv > 0 ? d.v[2] : 0);
      }
      const q = d.flip
        ? [corners[0], corners[3], corners[2], corners[1]]
        : [corners[0], corners[1], corners[2], corners[3]];
      // Split along the diagonal whose ends are more open — the standard fix
      // for AO interpolation anisotropy on the two triangles.
      const tris =
        q[0].ao + q[2].ao >= q[1].ao + q[3].ao
          ? [q[0], q[1], q[2], q[0], q[2], q[3]]
          : [q[1], q[2], q[3], q[1], q[3], q[0]];
      for (const t of tris) {
        pos.push(t.x * CELL_SIZE, t.y * CELL_SIZE, t.z * CELL_SIZE);
        norm.push(d.n[0], d.n[1], d.n[2]);
        pal.push(p);
        shade.push(AO_LEVELS[t.ao]);
      }
    }
  }
  if (pos.length === 0) return null;
  return { pos, norm, pal, shade };
}
